import { Box, Typography, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import cx from 'classnames'
import { Link } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React from 'react'

import Html from '../../components/Html/Html'
import { BLUE_BASE } from '../../theme/variables/colors'
import { useSideNavigationContext } from '../../utils/context/sideNavigationContext'
import { cleanString } from '../../utils/functions'

const MainContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
}))

const StyledTitle = styled(Typography)(({ theme }) => ({
  margin: '40px 0',
  paddingBottom: '24px',
  borderBottom: `3px solid ${BLUE_BASE}`,
  width: 'fit-content',
}))

const SubAdviserFilmLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: '#1A3967',
  fontSize: '1.875rem', // 30px
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2), // 71px
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2), // 60px
  },
}))

const ManagerCard = styled(Link)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  textDecoration: 'none',
  border: `1px solid ${BLUE_BASE}`,
  borderRadius: theme.shape.borderRadius * 2.5,
  padding: theme.spacing(4, 3),
  color: '#092A48',
  maxWidth: 320,
  width: '100%',
  backgroundColor: '#fff',
  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 15px rgba(0,0,0,0.2)',
  },
  [theme.breakpoints.down('md')]: {
    justifyItems: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2),
  },
}))

const ManagerCardContent = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}))

const ManagerTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.35rem',
  lineHeight: '2.5rem',
  color: BLUE_BASE,
  textAlign: 'center',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem', // 24px
    lineHeight: '2rem',
  },
}))

const ManagerRole = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  lineHeight: '1.5rem',
  color: '#092A48',
  marginBottom: theme.spacing(2),
}))

const Description = styled(Html)(({ theme }) => ({
  marginBottom: theme.spacing(6), // 47px
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginBottom: theme.spacing(5), // 40px
  },
}))

const CardsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(4),
  gridTemplateColumns: '1fr',
  justifyItems: 'center',
  [theme.breakpoints.up('md')]: {
    justifyItems: 'start',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  },
  margin: '32px auto',
}))

const PhotoWrapper = styled(Box)(({ theme }) => ({
  display: 'block',
  maxWidth: '156px',
  maxHeight: '156px',
  margin: '0 auto',
}))

const LogoWrapper = styled(Box)(({ theme }) => ({
  display: 'block',
  width: '200px',
  height: 'auto',
}))

const TopContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
  },
}))

const IMG_WRAPPER_STYLES = {
  borderRadius: '50%',
  width: '156px',
  height: '156px',
  objectFit: 'contain',
  margin: '0 auto',
}

const imageStyles = { maxWidth: '200px', width: '200px', height: 'auto' }

export default function FundManagement({
  product_subadviser,
  management_section,
  id,
  index,
}) {
  const isMobile = useMediaQuery('(max-width:768px)')
  const description =
    management_section?.html_content ||
    product_subadviser?.subadviser_firm[0]?.description
  const managers = product_subadviser?.managers ?? []
  const subadviser_firm = product_subadviser?.subadviser_firm[0]
  const url = subadviser_firm?.url

  const { createSectionRef } = useSideNavigationContext()

  return (
    <MainContainer ref={createSectionRef(index)}>
      <StyledTitle id={id} variant="h2">
        {management_section.title}
      </StyledTitle>
      <TopContainer>
        <SubAdviserFilmLink to={url} aria-label="Visit Sub-adviser Firm">
          <LogoWrapper>
            <GatsbyImage
              image={getImage(subadviser_firm?.logo?.localAsset)}
              alt={subadviser_firm?.logo?.description || 'Sub-advisor logo'}
              style={imageStyles}
            />
          </LogoWrapper>
        </SubAdviserFilmLink>
        <Description content={description} nowrap={true} />
      </TopContainer>
      <CardsGrid>
        {managers.map((manager, managerIndex) => {
          const { photo } = manager
          return (
            <ManagerCard
              key={managerIndex}
              id={cleanString(manager.url)}
              to={`/subadvisor${manager.url}`}
              className={cx({
                withoutPhoto: !photo,
              })}
              aria-label={`View details for ${manager.title}`}
            >
              <ManagerCardContent className={cx({ withoutPhoto: !photo })}>
                <PhotoWrapper>
                  {photo && (
                    <GatsbyImage
                      image={getImage(photo.localAsset)}
                      alt={manager.title}
                      style={IMG_WRAPPER_STYLES}
                      loading="lazy" // Ensure lazy loading
                    />
                  )}
                </PhotoWrapper>
                <ManagerTitle>{manager.title}</ManagerTitle>
                <ManagerRole>{manager.manager_job_title}</ManagerRole>
              </ManagerCardContent>
            </ManagerCard>
          )
        })}
      </CardsGrid>
    </MainContainer>
  )
}